import { Component, ComponentRef, ElementRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LoginService } from 'src/app/services/login/login.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalAuth } from 'src/app/utils/interfaces/new/localauth.interface';
import { OTPRequestObject, OTPVerifyObject, OTPResendObject, OTPRequestResponse, OTPVerifyErrorResponse } from 'src/app/utils/interfaces/new/otp.interface';
import { environment } from 'src/environments/environment';
import { EMAIL_REGEX } from 'src/app/utils/constante';
import { DroitsService } from 'src/app/services/droits/droits.service';
import { ConnectionType } from 'src/app/fragments/modal/handle-cpx-modal/handle-cpx-modal.component';
import { CpxHandleServiceAbstract, SessionTypeEnum, TypeConnecteurEnum } from 'src/app/services/cpx-handle/cpx-handle-service-abstract';
import { Tools } from 'src/app/utils/tools';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Utilisateur } from 'src/app/utils/interfaces/new/utilisateur.interface';
import { SnackbarService } from 'src/app/services/snackbar/snackbar.service';
import { AlertMessageFragment } from 'src/app/fragments/modal/alert-message/alert-message.fragment';
import { EtatDeploiementEnum } from 'src/app/utils/interfaces/new/cabinet.interface';
import { CguDecision, HandleCguComponent } from 'src/app/fragments/modal/handle-cgu/handle-cgu.component';
import { CguService, DecisionStateEnum } from 'src/app/services/cgu/cgu.service';
import { ConsultationsCache } from 'src/app/services/consultation/consultation.service';
import { ReferentielCache } from 'src/app/services/referentiel/referentiel.service';
import { set } from 'date-fns';

export enum LoginStepEnum {
  INITIAL = 'initial',
  OTP_REQUESTED = 'otp_requested',
  OTP_VERIFICATION = 'otp_verification'
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  private isMock = environment.mock;

  @ViewChild('password') password: ElementRef;
  public loginForm: FormGroup;
  public otpForm: FormGroup;
  public errorLogin = false;
  public errorOtp = false;
  public localAuths: LocalAuth[] = [];
  public search = '';
  public loading = false;
  public selectAccount = false;

  // OTP related properties
  public currentStep: LoginStepEnum = LoginStepEnum.INITIAL;
  public LoginStepEnum = LoginStepEnum;
  public otpSession: OTPRequestResponse | null = null;
  public otpTimeRemaining: number = 0;
  public otpTimerInterval: any;
  public otpExpired: boolean = false;
  public pendingLoginCredentials: { email: string, password: string, isFromSelected: boolean } | null = null;
  public otpCooldown: number = 70; //seconde
  public otpCooldownRemaining: number = 0;
  public otpCooldownInterval: any;

  // CPS
  public connectionType: ConnectionType = ConnectionType.unknow;
  captchaOK: boolean;
  public readonly recaptchaSiteKey = environment.recaptchaSiteKey;

  constructor(
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private userService: UserService,
    public router: Router,
    public viewContainerRef: ViewContainerRef,
    public modalService: ModalService,
    public authService: AuthService,
    public droitService: DroitsService,
    public cpxHandleService: CpxHandleServiceAbstract,
    public oauthService: OAuthService,
    private activatedRoute: ActivatedRoute,
    private _snack: SnackbarService,
    private _cguSrv: CguService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.loginForm = this.formBuilder.group({
      login: ['', Validators.pattern(EMAIL_REGEX)],
      accountSelected: [''],
      password: ['', Validators.required],
      cabinet: ['']
    });

    this.otpForm = this.formBuilder.group({
      otpCode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]]
    });

    this.localAuths = this.loginService.findAuth();
    await this.initPSC();

    this.activatedRoute.queryParams.subscribe(async (params: Params) => {
      if (params.type === 'psc' && params.state && params.code) {
        const loadingModal = this.modalService.openLoading(this.viewContainerRef);
        // TODO [PSC - point 1] Ré-authentification PSC en cours de session (ex: depuis Dossier Patient / DICA) :
        // const isReauthHandled = await this._handlePscReauthInSession(params.code, params.state);
        // if (isReauthHandled) {
        //   return;
        // }

        // Si il l'utilisateur est connecté actuellement alors il s'agit d'un linkage de compte PSC
        // et on le redirige vers mode-de-connexion-settings
        const profil = this.loginService.getCurrentProfil();
        if (profil != null && profil.actif && !profil.disabled && profil.cabinet) {
          // On redirige vers mode-de-connexion-settings avec tous les paramètres
          this.router.navigate(['/user/settings/modeDeConnexion'], {
            queryParams: {
              returnType: 'psc',
              code: params.code,
              state: params.state,
              session_state: params.session_state,
              iss: params.iss,
            }
          });
          return;
        }

        // Sinon c'est un login normal
        try {
          await this.loginWithPSC(params.state, params.code);
        } catch (e) {
          console.error(e);
          this.modalService.openAlert(this.viewContainerRef, 'Erreur',
            `Erreur lors du login avec ProSantéConnect.\n Détail : ${e.message}`);
            // Remove query params
          this.router.navigate([], {
            queryParams: {
              // tslint:disable-next-line:object-literal-key-quotes
              'type': null,
              // tslint:disable-next-line:object-literal-key-quotes
              'state': null,
              // tslint:disable-next-line:object-literal-key-quotes
              'code': null,
              // tslint:disable-next-line:object-literal-key-quotes
              'session_state': null,
            },
            queryParamsHandling: 'merge'
          });
        } finally {
          this.modalService.close(loadingModal);
        }
      }
    });

    this.cpxHandleService.closeSession(SessionTypeEnum.CARTE);
    this.cpxHandleService.closeSession(SessionTypeEnum.CERT_DMP);
    this.cpxHandleService.closeSession(SessionTypeEnum.CERT_INSI);

    // try {
    //   const continueLogin = await this._handleCgu('350514ba-6527-475f-9b2d-0854e922d4a7');
    //   if (!continueLogin || continueLogin.continueLogin === false) {
    //     return;
    //   }
    // } finally { }

    ConsultationsCache.clearConsultationsCache();
    ReferentielCache.clearAllReferentiel();
  }

  public async initPSC(): Promise<void> {
    const authCodeFlowConfig: AuthConfig = {
      issuer: environment.pscIssuer,
      redirectUri: environment.sitePath + '/login?type=psc',
      postLogoutRedirectUri: environment.sitePath + '/login',
      // Pas dans une popup car mal géré par la librairie
      // (tente de faire absolument un login alors qu'il doit être fait coté serveur)
      // silentRefreshRedirectUri: environment.sitePath + '/silent-refresh.html',
      clientId: environment.pscClientId,
      responseType: 'code',
      scope: 'openid scope_all',
      // oidc: false,
      showDebugInformation: true,
      strictDiscoveryDocumentValidation: false,
      customQueryParams: {
        acr_values: environment.pscOIDACR
      },
      disablePKCE: true, // HACK trop compliqué à gérer pour le moment (le plugin n'étant pas ouf sur ce point, et PSC le supportant par defaut mais non mandatory)
    };
    this.oauthService.configure(authCodeFlowConfig);
    await this.oauthService.loadDiscoveryDocument(environment.pscDiscorveryDocument);
  }

  async loginWithPSC(state: string, code: string): Promise<void> {
    let user: Utilisateur;
    let message: string;
    let authId: string;
    let authType= 'psc';
    try {
      const loginObject = await this.authService.loginWithPSC(code);
      user = loginObject.user;
      message = loginObject.message;
      authId = loginObject.authID;
    } catch (e) {
      this._snack.openSnackBar(e);
    }
    if (user) {
      await this.checkUserAndLogin(user, message, authType ,authId );
    } else {
      this.errorLogin = true;
      setTimeout(() => {
        this.errorLogin = false;
      }, 5000);
      throw Error('Erreur au login PSC');
    }
  }

  public async pscLogin(): Promise<void> {
    try {
      // TODO Save state & nonce
      await this.oauthService.initLoginFlow();
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * TODO [PSC - point 1] Gestion de la ré-authentification PSC en cours de session (DICA / Dossier patient).
   * Permet de valider l'authentification PSC sans déconnecter le praticien ni réinitialiser sa session courante.
   */
  private async _handlePscReauthInSession(code: string, state: string): Promise<boolean> {
    /*
    const isReauth = localStorage.getItem('psc_reauth_in_progress') === 'true';
    if (!isReauth) {
      return false;
    }
    localStorage.removeItem('psc_reauth_in_progress');

    try {
      // 1. Échanger le code auprès du backend (sans écraser la session courante)
      const loginObject = await this.authService.loginWithPSC(code);
      if (loginObject && loginObject.authID) {
        this.loginService.saveAuthTechInfo(loginObject.authID, 'psc');
      }

      // 2. Cas Popup (recommandé pour DICA) : notifier la fenêtre mère et fermer la fenêtre
      if (window.opener) {
        window.opener.postMessage({
          type: 'PSC_REAUTH_SUCCESS',
          authId: loginObject?.authID
        }, window.location.origin);
        window.close();
        return true;
      }

      // 3. Cas Redirection : retour direct vers le dossier patient
      const returnUrl = localStorage.getItem('psc_return_url') || '/user/patient';
      localStorage.removeItem('psc_return_url');
      this.router.navigateByUrl(returnUrl);
      return true;
    } catch (e) {
      console.error('[PSC Reauth] Échec de la ré-authentification :', e);
      if (window.opener) {
        window.opener.postMessage({ type: 'PSC_REAUTH_ERROR', error: (e as any)?.message }, window.location.origin);
        window.close();
        return true;
      }
      return false;
    }
    */
    return false;
  }

  public fillEmail(account: LocalAuth): void {
    if (account.utilisateur && account.utilisateur.email && this.loginForm.controls.accountSelected.value) {
      this.selectAccount = true;
      this.loginForm.controls.login.patchValue(account.utilisateur.email);
      this.selectAccount = false;
    }
    return;
  }

  public uncheckRadio(): void {
    if (!this.selectAccount && this.loginForm.controls.accountSelected.value) {
      this.loginForm.controls.accountSelected.patchValue(null);
    }
  }

  private async _handleCgu(signataire: Utilisateur): Promise<{ continueLogin: boolean }> {
    const alertRefushistorique = () => {
      this.modalService.openAlert(
        this.viewContainerRef, 'Erreur',
        `Vous n'avez pas accepté les CGU.\nVeuillez contacter le support Hypermed pour débloquer la validation des CGU.`
      );
    }

    const alertRefus = () => {
      this.modalService.openAlert(
        this.viewContainerRef, 'Erreur', 'Vous devez accepter les CGU pour accéder aux services Hypermed.'
      );
    }

    if (signataire.admin) {
      return { continueLogin: true };
    }

    const cguDecision = await this._cguSrv.getCguDecision(signataire.id);

    if (cguDecision.currentCguDecisionState === DecisionStateEnum.Accepte) {
      return { continueLogin: true };
    } else if (cguDecision.currentCguDecisionState === DecisionStateEnum.Refuse) {
      alertRefushistorique();
      return { continueLogin: false };
    }

    const cguModal = this.modalService.openModal(
      this.viewContainerRef, HandleCguComponent
    );

    cguModal.instance.cguContentSetter = cguDecision.currentCguContent;

    return new Promise<{ continueLogin: boolean }>((resolve) => {
      const sub = cguModal.instance.closeEmitter.subscribe(async (res: CguDecision) => {
        sub.unsubscribe();
        cguModal.destroy();
        if (res && res.isAccepted === true) {
          // Save decision
          await this._cguSrv.saveCguDecision(signataire.id, DecisionStateEnum.Accepte);
          resolve({ continueLogin: true });
        } else if (res && res.isAccepted === false) {
          alertRefus();
          // Save decision
          await this._cguSrv.saveCguDecision(signataire.id, DecisionStateEnum.Refuse);
          resolve({ continueLogin: false });
        } else {
          // Save decision
          await this._cguSrv.saveCguDecision(signataire.id, DecisionStateEnum.NonSigne);
          resolve({ continueLogin: false });
        }
      });
    });
  }

  public async onSubmit(): Promise<void> {
    this.loginForm.markAllAsTouched();
    if (this.loginForm.valid) {
      const loadingModal = this.modalService.openLoading(this.viewContainerRef);
      try {
        if (this.loginForm.controls.accountSelected.value) {
          await this.initiateLoginWithOtp(
            this.loginForm.controls.accountSelected.value.utilisateur.email,
            this.loginForm.controls.password.value,
            true
          );
        } else if (this.loginForm.controls.login.value) {
          await this.initiateLoginWithOtp(
            this.loginForm.controls.login.value,
            this.loginForm.controls.password.value,
            false
          );
        }
      } catch (e) {
        if (e.error === 'MustChangePassword') {
          let toChangeMail = '';
          if (this.loginForm.controls.accountSelected.value) {
            toChangeMail = this.loginForm.controls.accountSelected.value.utilisateur.email;
          } else {
            toChangeMail = this.loginForm.controls.login.value;
          }
          this.router.navigateByUrl(`/renew-password/${encodeURI(toChangeMail)}`);
        } else {
          this.errorLogin = true;
          setTimeout(() => {
            this.errorLogin = false;
          }, 5000);
        }
      } finally {
        this.modalService.close(loadingModal);
      }
    }
  }

  public async initiateLoginWithOtp(email: string, password: string, isFromSelected: boolean): Promise<void> {
    if (!this.ensureCaptchaValidated()) {
      return;
    }

    try {
      // Première étape : demander un OTP
      const otpRequest: OTPRequestObject = {
        type: 'email',
        payload: {
          email,
          password
        }
      };

      const loginResult = await this.authService.requestOtp(otpRequest);
      if ('id' in loginResult && 'email' in loginResult) {
        // uniquement si pas de 2FA actif
        // await this.checkUserAndLogin(loginResult as Utilisateur);
        await this.loginWithCredentials(isFromSelected);
      } else {
        this.otpSession = loginResult as OTPRequestResponse;
        this.pendingLoginCredentials = { email, password, isFromSelected };
        this.currentStep = LoginStepEnum.OTP_REQUESTED;
        this.startOtpTimer();
        this.startCooldown();

        this._snack.openSnackBar(this.otpSession.message);
      }
    } catch (error) {
      // Si l'OTP n'est pas requis, continuer avec le login normal
      if (error?.error?.errorCode === 'MustChangePassword') {
        throw { error: 'MustChangePassword' };
      } else if (error.status === 401) {
        this.errorLogin = true;
        setTimeout(() => {
          this.errorLogin = false;
        }, 5000);
      } else {
        // Fallback vers le login normal si l'OTP n'est pas disponible
        await this.loginWithCredentials(isFromSelected);
      }
    }
  }

  public async onOtpSubmit(): Promise<void> {
    this.otpForm.markAllAsTouched();
    if (this.otpForm.valid && this.otpSession) {
      const loadingModal = this.modalService.openLoading(this.viewContainerRef);
      try {
        const otpVerify: OTPVerifyObject = {
          sessionId: this.otpSession.sessionId,
          otpCode: this.otpForm.controls.otpCode.value
        };

        const result = await this.authService.verifyOtp(otpVerify);
        if (result.user) {
          this.stopOtpTimer();
          const isFromSelected = !!this.pendingLoginCredentials?.isFromSelected;
          await this.loginWithCredentials(isFromSelected);
          //await this.checkUserAndLogin(result.user, result.message, 'email', result.authID);
        }
      } catch (error) {
        const verifyError = error?.error as OTPVerifyErrorResponse;
        if ((verifyError?.errorCode === 'INVALID_CODE' || verifyError?.errorCode === 'CODE_BLOCKED') && this.otpSession) {
          this.otpSession.attemptsRemaining = verifyError.attemptsRemaining;
        }
        this.errorOtp = true;
        setTimeout(() => {
          this.errorOtp = false;
        }, 5000);
      } finally {
        this.modalService.close(loadingModal);
      }
    }
  }

  public async resendOtp(): Promise<void> {
    if (!this.otpSession || this.otpCooldownRemaining > 0) return;

    const loadingModal = this.modalService.openLoading(this.viewContainerRef);
    try {
      const otpResend: OTPResendObject = {
        sessionId: this.otpSession.sessionId
      };

      this.otpSession = await this.authService.resendOtp(otpResend);
      this.otpExpired = false;
      this.startOtpTimer();
      this.startCooldown();

      this._snack.openSnackBar(this.otpSession.message);
    } catch (error) {
      this._snack.openSnackBar('Erreur lors du renvoi du code OTP');
    } finally {
      this.modalService.close(loadingModal);
    }
  }

  public backToLogin(): void {
    this.currentStep = LoginStepEnum.INITIAL;
    this.otpSession = null;
    this.pendingLoginCredentials = null;
    this.stopOtpTimer();
    this.otpExpired = false;
    this.otpForm.reset();
    this.errorOtp = false;
  }

  private startOtpTimer(): void {
    this.stopOtpTimer();
    if (this.otpSession && this.otpSession.expiresAt) {
      const expirationTime = new Date(this.otpSession.expiresAt).getTime();
      this.otpTimerInterval = setInterval(() => {
        const now = new Date().getTime();
        this.otpTimeRemaining = Math.max(0, Math.floor((expirationTime - now) / 1000));
        if (this.otpTimeRemaining <= 0) {
          this.stopOtpTimer();
          this.otpExpired = true;
        }
      }, 1000);
    }
  }

  private stopOtpTimer(): void {
    if (this.otpTimerInterval) {
      clearInterval(this.otpTimerInterval);
      this.otpTimerInterval = null;
    }
  }

  public getOtpTimeFormatted(): string {
    const minutes = Math.floor(this.otpTimeRemaining / 60);
    const seconds = this.otpTimeRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  public async loginWithEmail(): Promise<void> {
    await this.loginWithCredentials(false);
  }

  public async loginWithSelected(): Promise<void> {
    await this.loginWithCredentials(true);
  }

  public async cpsLogin(): Promise<void> {
    if (!(await this.cpxHandleService.showCpxModal(this.viewContainerRef)).isSessionOpen) {
      return;
    }

    const cardStatus = await this.cpxHandleService.getCpxCardStatus(this.viewContainerRef, SessionTypeEnum.CARTE, TypeConnecteurEnum.DMPConnect);

    const randomMessage = Tools.randomString(20);
    const cpxSession = await this.cpxHandleService.getCpxSession(this.viewContainerRef, SessionTypeEnum.CARTE, TypeConnecteurEnum.DMPConnect);
    if (cpxSession) {
      const cpxCardSignature = await this.cpxHandleService.signWithCpx(this.viewContainerRef, randomMessage, cpxSession.pinCode, undefined, SessionTypeEnum.CARTE, TypeConnecteurEnum.DMPConnect);
      const user = await this.authService.loginWithCpx(
        cardStatus.s_cardId,
        randomMessage,
        cpxCardSignature.s_signature,
        cpxSession.psName,
      );
      if (user) {
        await this.checkUserAndLogin(user);
      } else {
        this.errorLogin = true;
        setTimeout(() => {
          this.errorLogin = false;
        }, 5000);
      }
    }
  }

  async checkUserAndLogin(user: Utilisateur, message?: string, authType?:string, authId?:string): Promise<void> {
    if (!user) {
      return;
    }

    const continueLogin = await this._handleCgu(user);
    if (!continueLogin || continueLogin.continueLogin === false) {
      return;
    }

    this.clearCurrentSession();
    if (user.disabled) {
      this.modalService.openAlert(this.viewContainerRef, 'Erreur', 'Vous avez été désactivé. Veuillez contacter un administrateur.');
    } else {
      if (message) {
        await this.handleMessageAfterLogin(message);
      }
      this.loginService.saveCurrentUser(user);
      if (user.admin) {
        this.loginService.saveAuth({utilisateur: user});
        this.router.navigateByUrl('/administration');
      } else {
        if (authType && authId) {
          this.loginService.saveAuthTechInfo(authId, authType);
        }
        const profils = await this.userService.findMyProfils();
        if (profils.length !== 0) {
          if (profils.length > 1) {
            this.router.navigateByUrl(`/select-profil/${user.id}`);
          } else {
            await this.handleProfilLogin(user, profils[0].id);
          }
        } else {
          this.modalService.openAlert(
            this.viewContainerRef,
            'Erreur',
            'Il semble que vous ne soyez associé à aucun cabinet pour le moment. Veuillez contacter un administrateur.'
          );
        }
      }
    }
  }

  async handleMessageAfterLogin(message: string): Promise<void> {
    return new Promise<void>(resolve => {
      let alertMessage: ComponentRef<AlertMessageFragment>;
      switch (message) {
        case 'RAPPROCHEMENT':
          alertMessage = this.modalService.openAlert(this.viewContainerRef, 'Rapprochement RPPS', 'Votre compte a été rapproché avec votre compte ProSantéConnect.');
          break;
        default:
          console.log('Unknown message after login: ' + message);
      }
      alertMessage.instance.closeEmitter.subscribe(() => {
        resolve();
      });
    });
  }

  resolved(captchaResponse: string): void {
    if (captchaResponse) {
      this.captchaOK = true;
    }
  }

  private ensureCaptchaValidated(): boolean {
    if (this.captchaOK) {
      return true;
    }
    this._snack.openSnackBarError('Vous devez valider le captcha pour vous connecter.');
    return false;
  }

  private async handleProfilLogin(user: Utilisateur, profilId: string): Promise<void> {
    const currentProfil = await this.authService.selectProfil(profilId);
    //on enregistre/Mise a jour le profil reçu
    const localAuth: LocalAuth = {utilisateur: user,  profil: currentProfil};
    this.loginService.saveCurrentProfil(currentProfil);
    this.loginService.saveAuth(localAuth);

    const combinatedRights = await this.droitService.findMyCombinedDroits();
    if (!combinatedRights.espacePerso) {
      this.router.navigateByUrl(`/select-docteur`);
    } else {
      if (currentProfil) {
        if (!currentProfil.cabinet) {
          this.router.navigateByUrl(`/stop-page/generic`);
        } else if (currentProfil.cabinet.disabled) {
          this.router.navigateByUrl(`/stop-page/disabled`);
        } else if (currentProfil.cabinet.etatDeploiement !== EtatDeploiementEnum.Deploye) {
          this.router.navigateByUrl('/stop-page/not-deployed');
        } else {
          this.router.navigateByUrl('/home');
        }
      } else {
        this.router.navigateByUrl('/stop-page/generic');
      }
    }
  }

  private clearCurrentSession(): void {
    this.loginService.removeAuthTechInfo();
    this.loginService.removeCurrentUser();
    this.loginService.removeCurrentProfil();
    this.loginService.removeCurrentDelegataireProfil();
    this.loginService.removeCurrentDelegataireUser();
  }

  public async loginWithCredentials(isFromSelected: boolean): Promise<void> {
    //verif captcha
    if (!this.ensureCaptchaValidated()) {
      return;
    }

    const email = isFromSelected
      ? this.loginForm.controls.accountSelected.value.utilisateur.email
      : this.loginForm.controls.login.value;
    const password = this.loginForm.controls.password.value;

    const user = await this.authService.login(email, password);

    if (user) {
      this.loginService.resetSessionFlags();
      //verif CGU
      const continueLogin = await this._handleCgu(user);
      if (!continueLogin || continueLogin.continueLogin === false) {
        return;
      }

      this.clearCurrentSession();


      if (user.disabled) {
        this.modalService.openAlert(this.viewContainerRef, 'Erreur', 'Vous avez été désactivé. Veuillez contacter un administrateur.');
      } else {
        this.loginService.saveCurrentUser(user);


        if (user.admin) {
          this.loginService.saveAuth({ utilisateur: user });
          this.router.navigateByUrl('/administration');
          return;
        }

        // ICI la seule différence email ou selected
        //Cas selected
        if (isFromSelected) {
          const profilId = this.loginForm.controls.accountSelected.value.profil.id;
          await this.handleProfilLogin(user, profilId);
          return;
        }

        //cas email → récupération profils
        const profils = await this.userService.findMyProfils();
        //pas de profil on sort
        if (!profils || profils.length === 0) {
          this.modalService.openAlert(
            this.viewContainerRef,
            'Erreur',
            'Il semble que vous ne soyez associé à aucun cabinet pour le moment. Veuillez contacter un administrateur.'
          );
          return;
        }
        //plusieur profil l'user doit choisir
        if (profils.length > 1) {
            this.router.navigateByUrl(`/select-profil/${user.id}`);
            return;
        }
        // si un seul profil on fait selection auto
        await this.handleProfilLogin(user, profils[0].id);
      }
    } else {
      this.errorLogin = true;
      setTimeout(() => {
        this.errorLogin = false;
      }, 5000);
    }
  }

  private startCooldown(): void {
    this.otpCooldownRemaining = this.otpCooldown;

    this.otpCooldownInterval = setInterval(() => {
      this.otpCooldownRemaining--;

      if (this.otpCooldownRemaining <= 0) {
        clearInterval(this.otpCooldownInterval);
        this.otpCooldownInterval = null;
      }
    }, 1000);
  }
}
